import FetchBase from '../Api/FetchBase'
import { observable, action } from 'mobx'
import {
  API_ENDPOINTS,
  DEFAULT_PAGE_START,
  DEFAULT_PER_PAGE_RECORD,
  API_CONSTANTS,
} from '../Utils/constants'

import moment from 'moment-timezone'
import {
  getUtcUnixDate,
  getCurrentTimeMoment,
  getStartDate,
  validateCondition,
} from '../Utils/rules'

export default class ReportingStore extends FetchBase {
  @observable ordering = undefined
  @observable orderingBy = undefined

  @observable feedbackLoader = false
  @observable feedbackPageNo = DEFAULT_PAGE_START
  @observable feedbackSearchText = ''
  @observable feedbackTotal = 0
  @observable feedbackFilters = {}
  @observable feedbacks = []

  @observable projectLeadsOrdering = undefined
  @observable projectLeadsOrderingBy = undefined
  @observable projectLeadsFilters = {}
  @observable projectLeadsSearchText = ''
  @observable projectLeadsLoader = false
  @observable projectLeadsPageNo = DEFAULT_PAGE_START
  @observable projectLeadsTotal = 0
  @observable projectLeadsList = []

  @observable projectLeadDetailsOrdering = undefined
  @observable projectLeadDetailsOrderingBy = undefined
  @observable plSelectedDateChoice = undefined
  @observable projectLeadDetailsFilters = {}
  @observable projectLeadDetailsSearchText = ''
  @observable projectLeadDetailsLoader = false
  @observable projectLeadDetailsPageNo = DEFAULT_PAGE_START
  @observable projectLeadDetailsTotal = 0
  @observable projectLeadDetailsList = []
  @observable projectChart = []
  @observable projectChartTotalProjects = 0
  @observable projectChartLoader = false

  @observable projectCloseOpenActionOrdering = undefined
  @observable projectCloseOpenActionOrderingBy = undefined
  @observable projectCloseOpenActionFilters = {}
  @observable projectCloseOpenActionSearchText = ''
  @observable projectCloseOpenActionPageNo = DEFAULT_PAGE_START
  @observable projectCloseOpenActionLoader = false
  @observable projectCloseOpenActionTotal = 0
  @observable projectCloseOpenActionList = []

  @observable onTimeProjectTimelineLoader = false
  @observable onTimeProjectTimelinePageNo = DEFAULT_PAGE_START
  @observable onTimeProjectTimelineDateCoice = undefined
  @observable onTimeProjectTimelineSearchText = ''
  @observable onTimeProjectTimelineTotal = 0
  @observable onTimeProjectTimelineFilters = {}
  @observable onTimeProjectTimeline = []

  @observable orderingDelayed = undefined
  @observable orderingDelayedBy = undefined
  @observable delayedProjectTimelineLoader = false
  @observable delayedProjectTimelinePageNo = DEFAULT_PAGE_START
  @observable delayedProjectTimelineDateChoice = undefined
  @observable delayedProjectTimelineSearchText = ''
  @observable delayedProjectTimelineTotal = 0
  @observable delayedProjectTimelineFilters = {}
  @observable delayedProjectTimeline = []

  @observable orderingDefaultReporting = undefined
  @observable orderingDefaultReportingBy = undefined
  @observable defaultReportingLoader = false
  @observable defaultReportingPageNo = DEFAULT_PAGE_START
  @observable defaultReportingSearchText = ''
  @observable defaultReportingTotal = 0
  @observable defaultReportingFilters = {}
  @observable defaultReporting = []

  @observable openActionItemsLoader = false
  @observable openActionItemsPageNo = DEFAULT_PAGE_START
  @observable openActionItemsSearchText = ''
  @observable openActionItemsTotal = 0
  @observable openActionItemsFilters = {}
  @observable openActionItems = []

  @observable orderingCompleted = undefined
  @observable orderingCompletedBy = undefined
  @observable completedActionItemsLoader = false
  @observable completedActionItemsPageNo = DEFAULT_PAGE_START
  @observable completedActionItemsSearchText = ''
  @observable completedActionItemsTotal = 0
  @observable completedActionItemsFilters = {}
  @observable completedActionItems = []

  @observable orderingOverdue = undefined
  @observable orderingOverdueBy = undefined
  @observable overdueActionItemsLoader = false
  @observable overdueActionItemsPageNo = DEFAULT_PAGE_START
  @observable overdueActionItemsSearchText = ''
  @observable overdueActionItemsTotal = 0
  @observable overdueActionItemsFilters = {}
  @observable overdueActionItems = []

  @observable pfSelectedOpenDateChoice = undefined
  @observable pfSelectedCompletedDateChoice = undefined
  @observable pfSelectedOverdueDateChoice = undefined

  @observable syncLoader = false
  @observable syncPageNo = DEFAULT_PAGE_START
  @observable syncDateChoice = undefined
  @observable onlinecDateChoice = undefined
  @observable syncSearchText = ''
  @observable syncTotal = 0
  @observable syncFilters = {}
  @observable sync = []

  @observable notificationReportFilters = {}
  @observable notificationReportSearchText = ''
  @observable notificationReportPageNo = DEFAULT_PAGE_START
  @observable notificationReportDateChoice = undefined
  @observable notificationReportLoader = false
  @observable notificationReportTotal = 0
  @observable notificationReportList = []

  @observable allNotificationUserForDropdown = []
  @observable allNotificationTypeForDropdown = []

  @action.bound
  setOrdering = (value = undefined) => {
    this.ordering = value
  }

  @action.bound
  setOrderingBy = (value = undefined) => {
    if (value === 'project') {
      value = 'projectName'
    } else if (value === 'actionitems') {
      value = 'title'
    }
    this.orderingBy = value
  }

  @action.bound
  setFeedbackFilters = (value = {}) => {
    this.feedbackFilters = value
  }

  @action.bound
  setFeedbackSearchText = (value = '') => {
    this.feedbackSearchText = value
  }

  @action.bound
  setFeedbackPageNo = (page = DEFAULT_PAGE_START) => {
    this.feedbackPageNo = page
  }

  @action.bound
  getFiltersForFeedback = () => {
    let temp = {}
    this.feedbackSearchText && (temp['search'] = this.feedbackSearchText)
    let keys = Object.keys(this.feedbackFilters || {})
    for (let filter of keys) {
      if (this.feedbackFilters[filter] !== undefined) {
        temp[filter] = this.feedbackFilters[filter]
      }
    }

    if (this.ordering) {
      temp['ordering'] = this.ordering
      temp['orderBy'] = this.orderingBy
    }

    return temp
  }

  @action.bound
  getAllFeedbacksExportData = async ({ exportFileType = 'csv' } = {}) => {
    try {
      let options = this.getFiltersForFeedback()
      options['exportFileType'] = exportFileType
      let response = await this.get(
        API_ENDPOINTS.REPORTING_FEEDBACK_EXPORT_DATA,
        {
          ...options,
          pageLimit: 0,
        }
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getAllFeedbacks = async (signal) => {
    try {
      this.feedbackLoader = true
      let filters = this.getFiltersForFeedback()
      let response = await this.get(
        API_ENDPOINTS.REPORTING_FEEDBACK,
        {
          ...filters,
          pageNo: Math.max(this.feedbackPageNo - 1, 0),
          pageLimit: DEFAULT_PER_PAGE_RECORD,
        },
        signal
      )
      if (response.status) {
        this.feedbackTotal = response?.data?.total || 0
        this.feedbacks = response?.data?.feedback || []
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      console.log(err, 66)
      return [false, undefined]
    } finally {
      this.feedbackLoader = false
    }
  }

  // Project Leads
  @action.bound
  setProjectLeadsOrdering = (value = undefined) => {
    this.projectLeadsOrdering = value
  }

  @action.bound
  setProjectLeadsOrderingBy = (value = undefined) => {
    if (value === 'project') {
      value = 'projectName'
    } else if (value === 'actionitems') {
      value = 'title'
    }
    this.projectLeadsOrderingBy = value
  }

  @action.bound
  setProjectLeadsFilters = (value = {}) => {
    this.projectLeadsFilters = value
  }

  @action.bound
  setProjectLeadsSearchText = (value = '') => {
    this.projectLeadsSearchText = value
  }

  @action.bound
  setProjectLeadsPageNo = (page = DEFAULT_PAGE_START) => {
    this.projectLeadsPageNo = page
  }

  @action.bound
  getFiltersForProjectLeads = () => {
    let temp = {}
    this.projectLeadsSearchText &&
      (temp['search'] = this.projectLeadsSearchText)
    let keys = Object.keys(this.projectLeadsFilters || {})
    for (let filter of keys) {
      if (this.projectLeadsFilters[filter] !== undefined) {
        temp[filter] = this.projectLeadsFilters[filter]
      }
    }

    if (this.projectLeadsOrdering) {
      temp['ordering'] = this.projectLeadsOrdering
      temp['orderBy'] = this.projectLeadsOrderingBy
    }

    return temp
  }

  @action.bound
  getAllProjectLeadsExportData = async ({ exportFileType = 'csv' } = {}) => {
    try {
      let options = this.getFiltersForProjectLeads()
      options['exportFileType'] = exportFileType
      let response = await this.post(
        API_ENDPOINTS.REPORTING_ALL_PROJECT_LEAD_EXPORT_DATA,
        {
          ...options,
          pageLimit: 0,
        }
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getAllProjectLeads = async (signal) => {
    try {
      this.projectLeadsLoader = true
      let filters = this.getFiltersForProjectLeads()
      let response = await this.post(
        API_ENDPOINTS.REPORTING_ALL_PROJECT_LEAD,
        {
          ...filters,
          pageNo: Math.max(this.projectLeadsPageNo - 1, 0),
          pageLimit: DEFAULT_PER_PAGE_RECORD,
        },
        signal
      )
      if (response.status) {
        this.projectLeadsTotal = response?.data?.total || 0
        this.projectLeadsList = response?.data?.projectLeadReport || []
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      console.log(err, 66)
      return [false, undefined]
    } finally {
      this.projectLeadsLoader = false
    }
  }

  @action.bound
  setProjectLeadDetailsOrdering = (value = undefined) => {
    this.projectLeadDetailsOrdering = value
  }

  @action.bound
  setProjectLeadDetailsOrderingBy = (value = undefined) => {
    this.projectLeadDetailsOrderingBy = value
  }

  @action.bound
  setSelectedDateChoice = (value) => {
    this.plSelectedDateChoice = value
  }

  @action.bound
  setProjectLeadDetailsFilters = (value = {}) => {
    this.projectLeadDetailsFilters = value
  }

  @action.bound
  setProjectLeadDetailsSearchText = (value = '') => {
    this.projectLeadDetailsSearchText = value
  }

  @action.bound
  setProjectLeadDetailsPageNo = (page = DEFAULT_PAGE_START) => {
    this.projectLeadDetailsPageNo = page
  }

  @action.bound
  getFiltersForProjectLeadDetails = () => {
    let temp = {}
    this.projectLeadDetailsSearchText &&
      (temp['search'] = this.projectLeadDetailsSearchText)
    let keys = Object.keys(this.projectLeadDetailsFilters || {})
    for (let filter of keys) {
      if (this.projectLeadDetailsFilters[filter] !== undefined) {
        temp[filter] = this.projectLeadDetailsFilters[filter]
      }
    }

    if (this.plSelectedDateChoice) {
      let dateFilter = getUtcUnixDate(this.plSelectedDateChoice, {
        start: temp['startDate'],
        end: temp['endDate'],
      })
      temp['startDate'] = dateFilter.start
      temp['endDate'] = dateFilter.end
    } else {
      delete temp['startDate']
      delete temp['endDate']
    }

    delete temp['dateChoice']

    if (this.projectLeadDetailsOrdering) {
      temp['ordering'] = this.projectLeadDetailsOrdering
      temp['orderBy'] = this.projectLeadDetailsOrderingBy
    }

    return temp
  }

  @action.bound
  getAllProjectLeadDetailsExportData = async ({
    onSiteLeadId,
    exportFileType = 'csv',
  } = {}) => {
    try {
      let options = this.getFiltersForProjectLeadDetails()
      options['exportFileType'] = exportFileType
      let response = await this.post(
        API_ENDPOINTS.REPORTING_PROJECT_LEAD_DETAILS_EXPORT_DATA,
        {
          ...options,
          onSiteLeadId,
          pageLimit: 0,
        }
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getAllProjectLeadDetails = async (onSiteLeadId, signal) => {
    try {
      this.projectLeadDetailsLoader = true
      let filters = this.getFiltersForProjectLeadDetails()
      let response = await this.post(
        API_ENDPOINTS.REPORTING_PROJECT_LEAD_DETAILS,
        {
          ...filters,
          onSiteLeadId,
          pageNo: Math.max(this.projectLeadDetailsPageNo - 1, 0),
          pageLimit: DEFAULT_PER_PAGE_RECORD,
        },
        signal
      )
      if (response.status) {
        this.projectLeadDetailsTotal = response?.data?.total || 0
        this.projectLeadDetailsList = response?.data?.projectLeadReport || []
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      console.log(err, 66)
      return [false, undefined]
    } finally {
      this.projectLeadDetailsLoader = false
    }
  }

  @action.bound
  resetProjectLeads = () => {
    this.plSelectedDateChoice = undefined
    this.projectLeadDetailsFilters = {}
  }

  @action.bound
  getProjectChartByOnsiteLeadId = async (onSiteLeadId, signal) => {
    try {
      this.projectChartLoader = true
      let response = await this.get(
        API_ENDPOINTS.REPORTING_ONSITE_LEAD_ANALYTICS_BY_ID,
        { onSiteLeadId },
        signal
      )
      if (response.status) {
        this.projectChart = response?.data?.projectStatusCount || []
        this.projectChartTotalProjects = response?.data?.totalProjectCount || 0
        return [true, response.data]
      } else {
        return [false, undefined]
      }
    } catch (err) {
      return [err, undefined]
    } finally {
      this.projectChartLoader = false
    }
  }
  // Project leads End

  @action.bound
  setProjectCloseOpenActionOrdering = (value = undefined) => {
    this.projectCloseOpenActionOrdering = value
  }

  @action.bound
  setProjectCloseOpenActionOrderingBy = (value = undefined) => {
    if (value === 'project') {
      value = 'projectName'
    }
    this.projectCloseOpenActionOrderingBy = value
  }

  @action.bound
  setProjectCloseOpenActionFilters = (value = {}) => {
    this.projectCloseOpenActionFilters = value
  }

  @action.bound
  setProjectCloseOpenActionSearchText = (value = '') => {
    this.projectCloseOpenActionSearchText = value
  }

  @action.bound
  setProjectCloseOpenActionPageNo = (page = DEFAULT_PAGE_START) => {
    this.projectCloseOpenActionPageNo = page
  }

  @action.bound
  getFiltersForProjectCloseOpenAction = () => {
    let temp = {}
    this.projectCloseOpenActionSearchText &&
      (temp['search'] = this.projectCloseOpenActionSearchText)
    let keys = Object.keys(this.projectCloseOpenActionFilters || {})
    for (let filter of keys) {
      if (this.projectCloseOpenActionFilters[filter] !== undefined) {
        temp[filter] = this.projectCloseOpenActionFilters[filter]
      }
    }

    if (this.projectCloseOpenActionOrdering) {
      temp['ordering'] = this.projectCloseOpenActionOrdering
      temp['orderBy'] = this.projectCloseOpenActionOrderingBy
    }

    return temp
  }

  @action.bound
  getAllProjectCloseOpenActionExportData = async ({
    exportFileType = 'csv',
  } = {}) => {
    try {
      let options = this.getFiltersForProjectCloseOpenAction()
      options['exportFileType'] = exportFileType
      let response = await this.get(
        API_ENDPOINTS.REPORTING_FEEDBACK_EXPORT_DATA,
        {
          ...options,
          pageLimit: 0,
        }
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getAllProjectCloseOpenAction = async () => {
    try {
      this.projectCloseOpenActionLoader = true
      let filters = this.getFiltersForProjectCloseOpenAction()
      let response = await this.get(API_ENDPOINTS.REPORTING_FEEDBACK, {
        ...filters,
        pageNo: Math.max(this.feedbackPageNo - 1, 0),
        pageLimit: DEFAULT_PER_PAGE_RECORD,
      })
      if (response.status) {
        this.projectCloseOpenActionTotal = response?.data?.total || 0
        this.projectCloseOpenActionList = response?.data?.feedback || []
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      console.log(err, 66)
      return [false, undefined]
    } finally {
      this.projectCloseOpenActionLoader = false
    }
  }

  @action.bound
  resetProjectCloseOpenAction = () => {
    this.projectCloseOpenActionFilters = {}
  }

  @action.bound
  setOnTimeProjectTimelineFilters = (value = {}) => {
    this.onTimeProjectTimelineFilters = value
  }

  @action.bound
  setOnTimeProjectTimelineSearchText = (value = '') => {
    this.onTimeProjectTimelineSearchText = value
  }

  @action.bound
  setOnTimeProjectTimelinePageNo = (page = DEFAULT_PAGE_START) => {
    this.onTimeProjectTimelinePageNo = page
  }

  @action.bound
  setDateChoiceForOnTimeProjecTimeline = (value = undefined) => {
    this.onTimeProjectTimelineDateCoice = value
  }

  @action.bound
  getFiltersForOnTimeProjectTimeline = () => {
    let temp = {}
    temp['projectTimeLineType'] = 'onTime'
    this.onTimeProjectTimelineSearchText &&
      (temp['search'] = this.onTimeProjectTimelineSearchText)
    let keys = Object.keys(this.onTimeProjectTimelineFilters || {})
    for (let filter of keys) {
      if (this.onTimeProjectTimelineFilters[filter] !== undefined) {
        temp[filter] = this.onTimeProjectTimelineFilters[filter]
      }
    }
    let { start, end } = getUtcUnixDate(this.onTimeProjectTimelineDateCoice, {
      start: temp?.startDate,
      end: temp?.endDate,
    })

    start ? (temp['startDate'] = start) : delete temp['startDate']
    end ? (temp['endDate'] = end) : delete temp['endDate']

    delete temp['choice']

    if (this.ordering) {
      temp['ordering'] = this.ordering
      temp['orderBy'] = this.orderingBy
    }

    return temp
  }

  @action.bound
  getOnTimeProjectTimelineExportData = async ({
    exportFileType = 'csv',
  } = {}) => {
    try {
      let options = this.getFiltersForOnTimeProjectTimeline()
      options['exportFileType'] = exportFileType
      let response = await this.get(
        API_ENDPOINTS.REPORTING_PROJECT_TIMELINE_EXPORT_DATA,
        {
          ...options,
          pageLimit: 0,
        }
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getOnTimeProjectTimeline = async (signal) => {
    try {
      this.onTimeProjectTimelineLoader = true
      let filters = this.getFiltersForOnTimeProjectTimeline()
      let response = await this.get(
        API_ENDPOINTS.REPORTING_PROJECT_TIMELINE,
        {
          ...filters,
          pageNo: Math.max(this.onTimeProjectTimelinePageNo - 1, 0),
          pageLimit: DEFAULT_PER_PAGE_RECORD,
        },
        signal
      )
      if (response.status) {
        this.onTimeProjectTimelineTotal = response?.data?.total || 0
        this.onTimeProjectTimeline =
          response?.data?.listReportProjectTimeline || []
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      console.log(err, 66)
      return [false, undefined]
    } finally {
      this.onTimeProjectTimelineLoader = false
    }
  }

  // start Delayed
  @action.bound
  setOrderingDelayed = (value = undefined) => {
    this.orderingDelayed = value
  }

  @action.bound
  setOrderingDelayedBy = (value = undefined) => {
    if (value === 'project') {
      value = 'projectName'
    } else if (value === 'actionitems') {
      value = 'title'
    }
    this.orderingDelayedBy = value
  }

  @action.bound
  setDelayedProjectTimelineFilters = (value = {}) => {
    this.delayedProjectTimelineFilters = value
  }

  @action.bound
  setDelayedProjectTimelineSearchText = (value = '') => {
    this.delayedProjectTimelineSearchText = value
  }

  @action.bound
  setDelayedProjectTimelinePageNo = (page = DEFAULT_PAGE_START) => {
    this.delayedProjectTimelinePageNo = page
  }

  @action.bound
  setDateChoiceForDelayedProjecTimeline = (value = undefined) => {
    this.delayedProjectTimelineDateChoice = value
  }

  @action.bound
  getFiltersForDelayedProjectTimeline = () => {
    let temp = {}
    temp['projectTimeLineType'] = 'delayed'
    this.delayedProjectTimelineSearchText &&
      (temp['search'] = this.delayedProjectTimelineSearchText)
    let keys = Object.keys(this.delayedProjectTimelineFilters || {})
    for (let filter of keys) {
      if (this.delayedProjectTimelineFilters[filter] !== undefined) {
        temp[filter] = this.delayedProjectTimelineFilters[filter]
      }
    }
    let { start, end } = getUtcUnixDate(this.delayedProjectTimelineDateChoice, {
      start: temp?.startDate,
      end: temp?.endDate,
    })

    start ? (temp['startDate'] = start) : delete temp['startDate']
    end ? (temp['endDate'] = end) : delete temp['endDate']

    delete temp['choice']

    if (this.orderingDelayed) {
      temp['ordering'] = this.orderingDelayed
      temp['orderBy'] = this.orderingDelayedBy
    }

    return temp
  }

  @action.bound
  getDelayedProjectTimelineExportData = async ({
    exportFileType = 'csv',
  } = {}) => {
    try {
      let options = this.getFiltersForDelayedProjectTimeline()
      options['exportFileType'] = exportFileType
      let response = await this.get(
        API_ENDPOINTS.REPORTING_PROJECT_TIMELINE_EXPORT_DATA,
        {
          ...options,
          pageLimit: 0,
        }
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getDelayedProjectTimeline = async (signal) => {
    try {
      this.delayedProjectTimelineLoader = true
      let filters = this.getFiltersForDelayedProjectTimeline()
      let response = await this.get(
        API_ENDPOINTS.REPORTING_PROJECT_TIMELINE,
        {
          ...filters,
          pageNo: Math.max(this.delayedProjectTimelinePageNo - 1, 0),
          pageLimit: DEFAULT_PER_PAGE_RECORD,
        },
        signal
      )
      if (response.status) {
        this.delayedProjectTimelineTotal = response?.data?.total || 0
        this.delayedProjectTimeline =
          response?.data?.listReportProjectTimeline || []
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      console.log(err, 66)
      return [false, undefined]
    } finally {
      this.delayedProjectTimelineLoader = false
    }
  }
  // End Delayed
  // Default start
  @action.bound
  setOrderingDefaultReporting = (value = undefined) => {
    this.orderingDefaultReporting = value
  }

  @action.bound
  setOrderingDefaultReportingBy = (value = undefined) => {
    if (value === 'project') {
      value = 'projectName'
    } else if (value === 'actionitems') {
      value = 'title'
    }
    this.orderingDefaultReportingBy = value
  }

  @action.bound
  setDefaultReportingFilters = (value = {}) => {
    this.defaultReportingFilters = value
  }

  @action.bound
  setDefaultReportingSearchText = (value = '') => {
    this.defaultReportingSearchText = value
  }

  @action.bound
  setDefaultReportingPageNo = (page = DEFAULT_PAGE_START) => {
    this.defaultReportingPageNo = page
  }

  @action.bound
  getFiltersForDefaultReporting = () => {
    let temp = {}
    temp['projectTimeLineType'] = 'notDefined'
    this.defaultReportingSearchText &&
      (temp['search'] = this.defaultReportingSearchText)
    let keys = Object.keys(this.defaultReportingFilters || {})
    for (let filter of keys) {
      if (this.defaultReportingFilters[filter] !== undefined) {
        temp[filter] = this.defaultReportingFilters[filter]
      }
    }

    if (this.orderingDefaultReporting) {
      temp['ordering'] = this.orderingDefaultReporting
      temp['orderBy'] = this.orderingDefaultReportingBy
    }

    return temp
  }

  @action.bound
  getDefaultReportingExportData = async ({ exportFileType = 'csv' } = {}) => {
    try {
      let options = this.getFiltersForDefaultReporting()
      options['exportFileType'] = exportFileType
      let response = await this.get(
        API_ENDPOINTS.REPORTING_PROJECT_TIMELINE_EXPORT_DATA,
        {
          ...options,
          pageLimit: 0,
        }
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getDefaultReporting = async () => {
    try {
      this.defaultReportingLoader = true
      let filters = this.getFiltersForDefaultReporting()
      let response = await this.get(API_ENDPOINTS.REPORTING_PROJECT_TIMELINE, {
        ...filters,
        pageNo: Math.max(this.defaultReportingPageNo - 1, 0),
        pageLimit: DEFAULT_PER_PAGE_RECORD,
      })
      if (response.status) {
        this.defaultReportingTotal = response?.data?.total || 0
        this.defaultReporting = response?.data?.listReportProjectTimeline || []
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      console.log(err, 66)
      return [false, undefined]
    } finally {
      this.defaultReportingLoader = false
    }
  }
  // End Default

  @action.bound
  setOpenActionItemsFilters = (value = {}) => {
    this.openActionItemsFilters = value
  }

  @action.bound
  setOpenActionItemsSearchText = (value = '') => {
    this.openActionItemsSearchText = value
  }

  @action.bound
  setOpenActionItemsPageNo = (page = DEFAULT_PAGE_START) => {
    this.openActionItemsPageNo = page
  }

  @action.bound
  setSelectedOpenDateChoice = (value) => {
    this.pfSelectedOpenDateChoice = value
  }

  @action.bound
  resetOpenFilterForm = () => {
    this.pfSelectedOpenDateChoice = undefined
  }

  @action.bound
  getFiltersForOpenActionItems = () => {
    let temp = {}
    temp['timezone'] = moment.tz.guess(true)

    this.openActionItemsSearchText &&
      (temp['search'] = this.openActionItemsSearchText)
    let keys = Object.keys(this.openActionItemsFilters || {})
    for (let filter of keys) {
      if (this.openActionItemsFilters[filter] !== undefined) {
        temp[filter] = this.openActionItemsFilters[filter]
      }
    }

    if (this.pfSelectedOpenDateChoice) {
      let dateFilter = getUtcUnixDate(this.pfSelectedOpenDateChoice, {
        start: temp['startDate'],
        end: temp['endDate'],
      })
      temp['startDate'] = dateFilter.start
      temp['endDate'] = dateFilter.end
    } else {
      temp['todayStartDate'] = getStartDate(getCurrentTimeMoment())
    }

    delete temp['dateChoice']
    if (this.ordering) {
      temp['ordering'] = this.ordering
      temp['orderBy'] = this.orderingBy
    } else {
      temp['ordering'] = 'DESC'
      temp['orderBy'] = 'endDate'
    }

    return temp
  }

  @action.bound
  getAllOpenActionItemsExportData = async ({ exportFileType = 'csv' } = {}) => {
    try {
      let options = this.getFiltersForOpenActionItems()
      options['exportFileType'] = exportFileType
      let response = await this.post(
        API_ENDPOINTS.REPORTING_ACTION_ITEMS_EXPORT_DATA,
        {
          ...options,
          actionStatusId: API_CONSTANTS.REPORTING_ACTION_OPEN,
          pageLimit: 0,
        }
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getAllOpenActionItems = async (signal) => {
    try {
      this.openActionItemsLoader = true
      let filters = this.getFiltersForOpenActionItems()
      let response = await this.post(
        API_ENDPOINTS.REPORTING_ACTION_ITEMS,
        {
          ...filters,
          pageNo: Math.max(this.openActionItemsPageNo - 1, 0),
          pageLimit: DEFAULT_PER_PAGE_RECORD,
          actionStatusId: API_CONSTANTS.REPORTING_ACTION_OPEN,
        },
        signal
      )
      if (response.status) {
        this.openActionItemsTotal = response?.data?.total || 0
        this.openActionItems = response?.data?.listReportActionItem || []
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      console.log(err, 66)
      return [false, undefined]
    } finally {
      this.openActionItemsLoader = false
    }
  }

  @action.bound
  setOrderingCompleted = (value = undefined) => {
    this.orderingCompleted = value
  }

  @action.bound
  setOrderingCompletedBy = (value = undefined) => {
    if (value === 'project') {
      value = 'projectName'
    } else if (value === 'actionitems') {
      value = 'title'
    }
    this.orderingCompletedBy = value
  }

  @action.bound
  setCompletedActionItemsFilters = (value = {}) => {
    this.completedActionItemsFilters = value
  }

  @action.bound
  setCompletedActionItemsSearchText = (value = '') => {
    this.completedActionItemsSearchText = value
  }

  @action.bound
  setCompletedActionItemsPageNo = (page = DEFAULT_PAGE_START) => {
    this.completedActionItemsPageNo = page
  }

  @action.bound
  setSelectedCompletedDateChoice = (value) => {
    this.pfSelectedCompletedDateChoice = value
  }

  @action.bound
  resetCompletedFilterForm = () => {
    this.pfSelectedCompletedDateChoice = undefined
  }

  @action.bound
  getFiltersForCompletedActionItems = () => {
    let temp = {}
    temp['timezone'] = moment.tz.guess(true)
    this.completedActionItemsSearchText &&
      (temp['search'] = this.completedActionItemsSearchText)
    let keys = Object.keys(this.completedActionItemsFilters || {})
    for (let filter of keys) {
      if (this.completedActionItemsFilters[filter] !== undefined) {
        temp[filter] = this.completedActionItemsFilters[filter]
      }
    }
    if (this.pfSelectedCompletedDateChoice) {
      let dateFilter = getUtcUnixDate(this.pfSelectedCompletedDateChoice, {
        start: temp['startDate'],
        end: temp['endDate'],
      })
      temp['startDate'] = dateFilter.start
      temp['endDate'] = dateFilter.end
    } else {
      temp['todayStartDate'] = getStartDate(getCurrentTimeMoment())
    }

    delete temp['dateChoice']

    if (this.orderingCompleted) {
      temp['ordering'] = this.orderingCompleted
      temp['orderBy'] = this.orderingCompletedBy
    } else {
      temp['ordering'] = 'DESC'
      temp['orderBy'] = 'endDate'
    }

    return temp
  }

  @action.bound
  getAllCompletedActionItemsExportData = async ({
    exportFileType = 'csv',
  } = {}) => {
    try {
      let options = this.getFiltersForCompletedActionItems()
      options['exportFileType'] = exportFileType
      let response = await this.post(
        API_ENDPOINTS.REPORTING_ACTION_ITEMS_EXPORT_DATA,
        {
          ...options,
          actionStatusId: API_CONSTANTS.REPORTING_ACTION_COMPLETED,
          pageLimit: 0,
        }
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getAllCompletedActionItems = async (signal) => {
    try {
      this.completedActionItemsLoader = true
      let filters = this.getFiltersForCompletedActionItems()
      let response = await this.post(
        API_ENDPOINTS.REPORTING_ACTION_ITEMS,
        {
          ...filters,
          pageNo: Math.max(this.completedActionItemsPageNo - 1, 0),
          pageLimit: DEFAULT_PER_PAGE_RECORD,
          actionStatusId: API_CONSTANTS.REPORTING_ACTION_COMPLETED,
        },
        signal
      )
      if (response.status) {
        this.completedActionItemsTotal = response?.data?.total || 0
        this.completedActionItems = response?.data?.listReportActionItem || []
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      console.log(err, 66)
      return [false, undefined]
    } finally {
      this.completedActionItemsLoader = false
    }
  }

  @action.bound
  setOrderingOverdue = (value = undefined) => {
    this.orderingOverdue = value
  }

  @action.bound
  setOrderingOverdueBy = (value = undefined) => {
    if (value === 'project') {
      value = 'projectName'
    } else if (value === 'actionitems') {
      value = 'title'
    }
    this.orderingOverdueBy = value
  }

  @action.bound
  setOverdueActionItemsFilters = (value = {}) => {
    this.overdueActionItemsFilters = value
  }

  @action.bound
  setOverdueActionItemsSearchText = (value = '') => {
    this.overdueActionItemsSearchText = value
  }

  @action.bound
  setOverdueActionItemsPageNo = (page = DEFAULT_PAGE_START) => {
    this.overdueActionItemsPageNo = page
  }

  @action.bound
  setSelectedOverdueDateChoice = (value) => {
    this.pfSelectedOverdueDateChoice = value
  }

  @action.bound
  resetOverdueFilterForm = () => {
    this.pfSelectedOverdueDateChoice = undefined
  }

  @action.bound
  getFiltersForOverdueActionItems = () => {
    let temp = {}
    temp['timezone'] = moment.tz.guess(true)

    this.overdueActionItemsSearchText &&
      (temp['search'] = this.overdueActionItemsSearchText)
    let keys = Object.keys(this.overdueActionItemsFilters || {})
    for (let filter of keys) {
      if (this.overdueActionItemsFilters[filter] !== undefined) {
        temp[filter] = this.overdueActionItemsFilters[filter]
      }
    }
    temp['delay'] = Math.max(temp['delay'] || 1, 1)

    if (this.pfSelectedOverdueDateChoice) {
      let dateFilter = getUtcUnixDate(this.pfSelectedOverdueDateChoice, {
        start: temp['startDate'],
        end: temp['endDate'],
      })
      temp['startDate'] = dateFilter.start
      temp['endDate'] = dateFilter.end
    } else {
      temp['todayStartDate'] = getStartDate(getCurrentTimeMoment())
    }

    delete temp['dateChoice']

    if (this.orderingOverdue) {
      temp['ordering'] = this.orderingOverdue
      temp['orderBy'] = this.orderingOverdueBy
    } else {
      temp['ordering'] = 'DESC'
      temp['orderBy'] = 'delayedDays'
    }

    return temp
  }

  @action.bound
  getAllOverdueActionItemsExportData = async ({
    exportFileType = 'csv',
  } = {}) => {
    try {
      let options = this.getFiltersForOverdueActionItems()
      options['exportFileType'] = exportFileType
      let response = await this.post(
        API_ENDPOINTS.REPORTING_ACTION_ITEMS_EXPORT_DATA,
        {
          ...options,
          actionStatusId: API_CONSTANTS.REPORTING_ACTION_OPEN,
          pageLimit: 0,
        }
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getAllOverdueActionItems = async (signal) => {
    try {
      this.overdueActionItemsLoader = true
      let filters = this.getFiltersForOverdueActionItems()

      let response = await this.post(
        API_ENDPOINTS.REPORTING_ACTION_ITEMS,
        {
          ...filters,
          pageNo: Math.max(this.overdueActionItemsPageNo - 1, 0),
          pageLimit: DEFAULT_PER_PAGE_RECORD,
          actionStatusId: API_CONSTANTS.REPORTING_ACTION_OPEN,
        },
        signal
      )
      if (response.status) {
        this.overdueActionItemsTotal = response?.data?.total || 0
        this.overdueActionItems = response?.data?.listReportActionItem || []
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      console.log(err, 66)
      return [false, undefined]
    } finally {
      this.overdueActionItemsLoader = false
    }
  }

  @action.bound
  setSyncFilters = (value = {}) => {
    this.syncFilters = value
  }

  @action.bound
  setSyncSearchText = (value = '') => {
    this.syncSearchText = value
  }

  @action.bound
  setSyncPageNo = (page = DEFAULT_PAGE_START) => {
    this.syncPageNo = page
  }

  @action.bound
  setDateChoiceForSync = (value = undefined) => {
    this.syncDateChoice = value
  }

  @action.bound
  setDateChoiceForOnline = (value = undefined) => {
    this.onlinecDateChoice = value
  }

  @action.bound
  getFiltersForSync = () => {
    let temp = {}
    this.syncSearchText && (temp['search'] = this.syncSearchText)
    temp['isCustomerRole'] = false
    let keys = Object.keys(this.syncFilters || {})
    for (let filter of keys) {
      if (this.syncFilters[filter] !== undefined) {
        temp[filter] = this.syncFilters[filter]
      }
    }

    if (this.syncDateChoice) {
      let dateFilter = getUtcUnixDate(this.syncDateChoice, {
        start: temp['syncStartDate'],
        end: temp['syncEndDate'],
      })
      temp['syncStartDate'] = dateFilter.start
      temp['syncEndDate'] = dateFilter.end
    } else {
      delete temp['syncStartDate']
      delete temp['syncEndDate']
    }

    delete temp['choice']

    if (this.onlinecDateChoice) {
      let dateFilter = getUtcUnixDate(this.onlinecDateChoice, {
        start: temp['onlineStartDate'],
        end: temp['onlineEndDate'],
      })
      temp['onlineStartDate'] = dateFilter.start
      temp['onlineEndDate'] = dateFilter.end
    } else {
      delete temp['onlineStartDate']
      delete temp['onlineEndDate']
    }

    delete temp['onlineChoice']

    if (this.ordering) {
      temp['ordering'] = this.ordering
      temp['orderBy'] = this.orderingBy
    }

    return temp
  }

  @action.bound
  getSyncExportData = async ({ exportFileType = 'csv' } = {}) => {
    try {
      let options = this.getFiltersForSync()
      options['exportFileType'] = exportFileType
      let response = await this.get(API_ENDPOINTS.REPORTING_SYNC_EXPORT_DATA, {
        ...options,
        pageLimit: 0,
      })
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getSync = async (signal) => {
    try {
      this.syncLoader = true
      let filters = this.getFiltersForSync()
      let response = await this.get(
        API_ENDPOINTS.REPORTING_SYNC,
        {
          ...filters,
          pageNo: Math.max(this.syncPageNo - 1, 0),
          pageLimit: DEFAULT_PER_PAGE_RECORD,
        },
        signal
      )
      if (response.status) {
        this.syncTotal = response?.data?.total || 0
        this.sync = response?.data?.userReports || []
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      console.log(err, 66)
      return [false, undefined]
    } finally {
      this.syncLoader = false
    }
  }

  // Notification Reporting
  @action.bound
  setNotificationReportFilters = (value = {}) => {
    this.notificationReportFilters = value
  }

  @action.bound
  setNotificationReportSearchText = (value = '') => {
    this.notificationReportSearchText = value
  }

  @action.bound
  setNotificationReportPageNo = (page = DEFAULT_PAGE_START) => {
    this.notificationReportPageNo = page
  }

  @action.bound
  setDateChoiceForNotificationReport = (value = undefined) => {
    this.notificationReportDateChoice = value
  }

  @action.bound
  getFiltersForNotificationReport = () => {
    let temp = {}
    this.notificationReportSearchText &&
      (temp['search'] = this.notificationReportSearchText)
    let keys = Object.keys(this.notificationReportFilters || {})
    for (let filter of keys) {
      if (this.notificationReportFilters[filter] !== undefined) {
        temp[filter] = this.notificationReportFilters[filter]
      }
    }

    if (this.notificationReportDateChoice) {
      let dateFilter = getUtcUnixDate(this.notificationReportDateChoice, {
        start: temp['notificationStartDate'],
        end: temp['notificationEndDate'],
      })
      temp['createdAtMin'] = dateFilter.start
      temp['createdAtMax'] = dateFilter.end
    } else {
      delete temp['createdAtMin']
      delete temp['createdAtMax']
    }

    delete temp['choice']
    delete temp['notificationStartDate']
    delete temp['notificationEndDate']

    if (this.ordering) {
      temp['ordering'] = this.ordering
      temp['orderBy'] = this.orderingBy
    }

    return temp
  }

  @action.bound
  getNotificationReportExportData = async ({ exportFileType = 'csv' } = {}) => {
    try {
      let options = this.getFiltersForNotificationReport()
      options['exportFileType'] = exportFileType
      let response = await this.post(
        API_ENDPOINTS.REPORTING_NOTIFICATION_EXPORT_DATA,
        {
          ...options,
          pageLimit: 0,
        }
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getNotificationReport = async (signal) => {
    try {
      this.notificationReportLoader = true
      let filters = this.getFiltersForNotificationReport()
      let response = await this.post(
        API_ENDPOINTS.REPORTING_NOTIFICATION,
        {
          ...filters,
          pageNo: Math.max(this.notificationReportPageNo - 1, 0),
          pageLimit: DEFAULT_PER_PAGE_RECORD,
        },
        signal
      )
      if (response.status) {
        this.notificationReportTotal = response?.data?.total || 0
        this.notificationReportList = response?.data?.notificationReports || []
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      console.log(err, 66)
      return [false, undefined]
    } finally {
      this.notificationReportLoader = false
    }
  }
  // End Notification
  // Dropdowns
  @action.bound
  getAllNotificationUserForDropdown = async (signal) => {
    try {
      let response = await this.post(
        API_ENDPOINTS.GET_DROPDONW_NOTIFICATION_USER,
        {},
        signal
      )
      if (response.status) {
        this.allNotificationUserForDropdown = response.data.usersData || []
        return true
      } else {
        return false
      }
    } catch (err) {
      return false
    }
  }

  @action.bound
  getAllNotificationTypeForDropdown = async (signal) => {
    try {
      let response = await this.post(
        API_ENDPOINTS.GET_DROPDONW_NOTIFICATION_TYPE,
        {},
        signal
      )
      if (response.status) {
        this.allNotificationTypeForDropdown =
          response.data.notificationTypes || []
        return true
      } else {
        return false
      }
    } catch (err) {
      return false
    }
  }

  @action.bound
  resetFeedbacks = () => {
    this.feedbackFilters = {}
  }

  @action.bound
  resetStore = () => {
    this.ordering = undefined
    this.orderingBy = undefined
    this.feedbackLoader = false
    this.feedbackPageNo = DEFAULT_PAGE_START
    this.feedbackSearchText = ''
    this.feedbackTotal = 0
    this.feedbackFilters = {}
    this.feedbacks = []

    this.projectLeadsOrdering = undefined
    this.projectLeadsOrderingBy = undefined
    this.projectLeadsFilters = {}
    this.projectLeadsSearchText = ''
    this.projectLeadsLoader = false
    this.projectLeadsPageNo = DEFAULT_PAGE_START
    this.projectLeadsTotal = 0
    this.projectLeadsList = []

    this.projectLeadDetailsOrdering = undefined
    this.projectLeadDetailsOrderingBy = undefined
    this.plSelectedDateChoice = undefined
    this.projectLeadDetailsFilters = {}
    this.projectLeadDetailsSearchText = ''
    this.projectLeadDetailsLoader = false
    this.projectLeadDetailsPageNo = DEFAULT_PAGE_START
    this.projectLeadDetailsTotal = 0
    this.projectLeadDetailsList = []
    this.projectChart = []
    this.projectChartTotalProjects = 0
    this.projectChartLoader = false

    this.projectCloseOpenActionOrdering = undefined
    this.projectCloseOpenActionOrderingBy = undefined
    this.projectCloseOpenActionFilters = {}
    this.projectCloseOpenActionSearchText = ''
    this.projectCloseOpenActionPageNo = DEFAULT_PAGE_START
    this.projectCloseOpenActionLoader = false
    this.projectCloseOpenActionTotal = 0
    this.projectCloseOpenActionList = []

    this.onTimeProjectTimelineLoader = false
    this.onTimeProjectTimelinePageNo = DEFAULT_PAGE_START
    this.onTimeProjectTimelineDateCoice = undefined
    this.onTimeProjectTimelineSearchText = ''
    this.onTimeProjectTimelineTotal = 0
    this.onTimeProjectTimelineFilters = {}
    this.onTimeProjectTimeline = []

    this.orderingDelayed = undefined
    this.orderingDelayedBy = undefined
    this.delayedProjectTimelineLoader = false
    this.delayedProjectTimelinePageNo = DEFAULT_PAGE_START
    this.delayedProjectTimelineDateChoice = undefined
    this.delayedProjectTimelineSearchText = ''
    this.delayedProjectTimelineTotal = 0
    this.delayedProjectTimelineFilters = {}
    this.delayedProjectTimeline = []

    this.orderingDefaultReporting = undefined
    this.orderingDefaultReportingBy = undefined
    this.defaultReportingLoader = false
    this.defaultReportingPageNo = DEFAULT_PAGE_START
    this.defaultReportingSearchText = ''
    this.defaultReportingTotal = 0
    this.defaultReportingFilters = {}
    this.defaultReporting = []

    this.openActionItemsLoader = false
    this.openActionItemsPageNo = DEFAULT_PAGE_START
    this.openActionItemsSearchText = ''
    this.openActionItemsTotal = 0
    this.openActionItemsFilters = {}
    this.openActionItems = []

    this.orderingCompleted = undefined
    this.orderingCompletedBy = undefined
    this.completedActionItemsLoader = false
    this.completedActionItemsPageNo = DEFAULT_PAGE_START
    this.completedActionItemsSearchText = ''
    this.completedActionItemsTotal = 0
    this.completedActionItemsFilters = {}
    this.completedActionItems = []

    this.orderingOverdue = undefined
    this.orderingOverdueBy = undefined
    this.overdueActionItemsLoader = false
    this.overdueActionItemsPageNo = DEFAULT_PAGE_START
    this.overdueActionItemsSearchText = ''
    this.overdueActionItemsTotal = 0
    this.overdueActionItemsFilters = {}
    this.overdueActionItems = []

    this.pfSelectedOpenDateChoice = undefined
    this.pfSelectedCompletedDateChoice = undefined
    this.pfSelectedOverdueDateChoice = undefined
    this.syncLoader = false
    this.syncPageNo = DEFAULT_PAGE_START
    this.syncDateChoice = undefined
    this.onlinecDateChoice = undefined
    this.syncSearchText = ''
    this.syncTotal = 0
    this.syncFilters = {}
    this.sync = []

    this.notificationReportFilters = {}
    this.notificationReportSearchText = ''
    this.notificationReportPageNo = DEFAULT_PAGE_START
    this.notificationReportDateChoice = undefined
    this.notificationReportLoader = false
    this.notificationReportTotal = 0
    this.notificationReportList = []
    this.allNotificationUserForDropdown = []
    this.allNotificationTypeForDropdown = []
  }
}
